import WelcomeView from '@Views/WelcomeView.vue';
import RequestStartupView from '@Views/RequestStartupView.vue';
import RequestExpiredView from '@Views/RequestExpiredView.vue';
import RequestCanceledView from '@Views/RequestCanceledView.vue';
import FraudEducationView from '@Views/FraudEducationView.vue';
import WhatsNextView from '@Views/WhatsNextView.vue';
import MppTransferAmountView from '@Views/send/MppTransferAmountView.vue';
import MppPurchaseDecisionView from '@Views/send/MppPurchaseDecision.vue';
import MppPaymentDetailsView from '@Views/send/MppPaymentDetails.vue';
import PaymentRequestOverviewView from '@Views/send/PaymentRequestOverview.vue';
import PaymentMethodsView from '@Views/send/PaymentMethodsView.vue';
import WireInstructionsReadyView from '@Views/send/WireInstructionsReady.vue';
import WireInstructionsView from '@Views/send/WireInstructions.vue';
import HeroPackageView from '@Views/send/HeroPackage.vue';
import ConfirmReviewView from '@Views/confirm/ConfirmReviewView.vue';
import ConfirmCompleteView from '@Views/confirm/ConfirmCompleteView.vue';
import RequestBlockedView from '@Views/RequestBlockedView.vue';
import CollectAccountDetails from '@Views/collect/CollectAccountDetails.vue';
import CollectReview from '@Views/collect/CollectReview.vue';
import CollectComplete from '@Views/collect/CollectComplete.vue';
import GeneralErrorView from '@Views/GeneralErrorView.vue';
import SubmittedView from '@Views/match/SubmittedView.vue';
import EndedView from '@Views/match/EndedView.vue';
import DocTypeSelectionView from '@Views/match/DocTypeSelectionView.vue';
import RetryView from '@Views/match/RetryView.vue';
import ProcessingCompleteView from '@Views/match/ProcessingCompleteView.vue';
import PermissionDeniedView from '@Views/match/PermissionDeniedView.vue';

/**
 * Route names reached only as an Intellicheck capture callback (a full-page
 * redirect mid-IDV flow). Exported so consumers reference the name by constant
 * instead of a duplicated string literal — a rename here is then a compile
 * error at every use site rather than a silent drift.
 */
export const MATCH_IDV_PROCESSING_COMPLETE_ROUTE = 'match-idv-processing-complete';
export const MATCH_IDV_VERIFICATION_ERROR_ROUTE = 'match-idv-verification-error';

export const createRoutes = (basePath: string) => {
    // TODO: Return list of routes and custom meta data if needed
    return [
        {
            path: `${basePath}/request/request-expired`,
            name: 'request-expired',
            component: RequestExpiredView,
        },
        {
            path: `${basePath}/request/request-canceled`,
            name: 'request-canceled',
            component: RequestCanceledView,
        },
        {
            path: `${basePath}/request/:requestType(send|confirm|collect|identity)/general-error`,
            name: 'general-error',
            component: GeneralErrorView,
        },
        {
            path: `${basePath}/request/fraud-education/:taskId`,
            name: 'fraud-education',
            component: FraudEducationView,
            meta: { terminal: true },
        },
        {
            path: `${basePath}/request/:requestType(send|confirm|collect|identity)/:token?`,
            name: 'welcome',
            component: WelcomeView,
            meta: { requiresFlow: true },
        },
        {
            path: `${basePath}/request/:requestType(send|confirm|collect|identity)/startup/:token?`,
            name: 'startup',
            component: RequestStartupView,
            meta: { bootstrap: true, requiresFlow: true },
        },
        {
            path: `${basePath}/request/:requestType(send|confirm|collect|identity)/whats-next`,
            name: 'whats-next',
            component: WhatsNextView,
            meta: { requiresFlow: true },
        },
        {
            path: `${basePath}/request/:requestType(send)/mpp-transfer-amount`,
            name: 'mpp-transfer-amount',
            component: MppTransferAmountView,
            meta: { requiresFlow: true },
        },
        {
            path: `${basePath}/request/:requestType(send)/mpp-purchase-decision`,
            name: 'mpp-purchase-decision',
            component: MppPurchaseDecisionView,
            meta: { requiresFlow: true },
        },
        {
            path: `${basePath}/request/:requestType(send)/mpp-payment-details`,
            name: 'mpp-payment-details',
            component: MppPaymentDetailsView,
            meta: { requiresFlow: true },
        },
        {
            path: `${basePath}/request/:requestType(send)/hero-package`,
            name: 'hero-package',
            component: HeroPackageView,
            meta: { requiresFlow: true },
        },
        {
            path: `${basePath}/request/:requestType(send)/payment-request-overview`,
            name: 'payment-request-overview',
            component: PaymentRequestOverviewView,
            meta: { requiresFlow: true },
        },
        {
            path: `${basePath}/request/:requestType(send)/payment-method-select`,
            name: 'payment-method-select',
            component: PaymentMethodsView,
            meta: { requiresFlow: true },
        },
        {
            path: `${basePath}/request/:requestType(send|confirm|collect)/wire-instructions-ready`,
            name: 'wire-instructions-ready',
            component: WireInstructionsReadyView,
            meta: { requiresFlow: true, loadRequestFlowOverride: true },
        },
        {
            // DS-2859: wire-instructions is the Send task's final participant-facing screen
            // (and the shared Send/Confirm/Collect download view). Mark as terminal so the
            // shared BackToDashboard component renders its "Back to Dashboard" CTA at the
            // bottom of the FlowLayout body — matching demo.certifid.com parity. Only consumer
            // of meta.terminal is BackToDashboard.vue:36 (BackToDashboard.isTerminalPage),
            // so this change is layout-only.
            path: `${basePath}/request/:requestType(send|confirm|collect)/wire-instructions`,
            name: 'wire-instructions',
            component: WireInstructionsView,
            meta: { requiresFlow: true, terminal: true },
        },
        {
            path: `${basePath}/request/:requestType(send|confirm|collect|identity)/request-blocked`,
            name: 'request-blocked',
            component: RequestBlockedView,
            // PLAT-2857: the collect submit pushes here right after the BFF blocks the request,
            // but the cached flow was loaded pre-submit and doesn't contain this screen — the
            // guard would next(false) the push. Forcing a flow reload picks up the blocked flow
            // (same mechanism as wire-instructions-ready).
            meta: { requiresFlow: true, loadRequestFlowOverride: true },
        },
        {
            path: `${basePath}/request/:requestType(confirm)/confirm-review`,
            name: 'confirm-review',
            component: ConfirmReviewView,
            meta: { requiresFlow: true },
        },
        {
            // DS-2859: confirm-complete is the Confirm task's final screen — bank details
            // confirmed. Surfaces BackToDashboard at the bottom of the layout body for parity
            // with demo.certifid.com. See wire-instructions above for the meta.terminal contract.
            path: `${basePath}/request/:requestType(confirm)/confirm-complete`,
            name: 'confirm-complete',
            component: ConfirmCompleteView,
            meta: { requiresFlow: true, terminal: true },
        },
        {
            path: `${basePath}/request/:requestType(collect)/collect-account-details`,
            name: 'collect-account-details',
            component: CollectAccountDetails,
            meta: { requiresFlow: true },
        },
        {
            path: `${basePath}/request/:requestType(collect)/collect-review`,
            name: 'collect-review',
            component: CollectReview,
            meta: { requiresFlow: true },
        },
        {
            // DS-2859: collect-complete is the Collect task's final screen — bank details
            // submitted. Surfaces BackToDashboard at the bottom of the layout body for parity
            // with demo.certifid.com. See wire-instructions above for the meta.terminal contract.
            path: `${basePath}/request/:requestType(collect)/collect-complete`,
            name: 'collect-complete',
            component: CollectComplete,
            meta: { requiresFlow: true, terminal: true },
        },
        {
            /**
             * Optional :token is present when a mobile user arrives via QR code scan from the desktop flow.
             * setupApp.ts extracts the token to bootstrap the activity session.
             *
             * DS-2859: desktop Match handoff lives here — the page shows the QR code the
             * participant scans with their phone to run Intellicheck capture. From the desktop
             * user's perspective, this is the terminal step (the rest of the verification
             * happens on mobile), so surface BackToDashboard. Mobile sessions arriving via the
             * QR scan don't get it from this gate: App.vue's getDashboardContext provider
             * returns participantPortalEnabled: false for a session holding a QR token it did
             * not mint. Note this is the only thing that gate does; it is not a guarantee the
             * scanning phone never reaches the dashboard. InviteRedirectBusiness redirects on
             * portal-enabled plus a resolvable participant token, without considering which
             * device is calling, and shouldSkipInviteDashboardRedirect does not exempt the
             * scan URL. See PS-1169 review Q2.
             */
            path: `${basePath}/request/:requestType(identity)/select-doc-type/:token?`,
            name: 'match-idv-select-doc-type',
            component: DocTypeSelectionView,
            meta: { requiresFlow: true, terminal: true },
        },
        {
            /**
             * Optional :token is present when Intellicheck redirects back after capture completes.
             * Request BFF constructs the redirect_url with the invite token so setupApp.ts
             * can re-bootstrap the session on the fresh page load.
             */
            path: `${basePath}/request/:requestType(identity)/processing-complete/:token?`,
            name: MATCH_IDV_PROCESSING_COMPLETE_ROUTE,
            component: ProcessingCompleteView,
            meta: { requiresFlow: true },
        },
        {
            path: `${basePath}/request/:requestType(identity)/retry`,
            name: 'match-idv-retry',
            component: RetryView
        },
        {
            path: `${basePath}/request/:requestType(identity)/submitted`,
            name: 'match-idv-submitted',
            component: SubmittedView,
        },
        {
            /**
             * Optional :token is present when Intellicheck redirects back after a capture error
             * (e.g., camera permission denied). Request BFF constructs the error_redirect_url
             * with the invite token so setupApp.ts can re-bootstrap the session.
             */
            path: `${basePath}/request/:requestType(identity)/verification-error/:token?`,
            name: MATCH_IDV_VERIFICATION_ERROR_ROUTE,
            component: PermissionDeniedView
        },
        {
            path: `${basePath}/request/:requestType(identity)/ended`,
            name: 'match-idv-ended',
            component: EndedView,
        },
    ];
};
